import { ReadonlyMat4, ReadonlyVec3, vec3 } from 'gl-matrix';
import { WindowLevel } from '~/domain/patient/windowLevel';
import { DicomImageSlice } from '../../../domain/dicom/dicomImageSlice';
import { Image3D } from '../../../domain/patient/image3d';
import { gl } from '../../Engine';
import { EnvironmentObject } from '../common/EnvironmentObject';
import { Grid } from '../common/Grid';

/** Max finite value in IEEE 754 half-precision float (R16F). */
const HALF_FLOAT_MAX = 65504;

/**
 * Cached OES_texture_float_linear probe result.
 * undefined = not yet checked, null = unavailable.
 * Required for LINEAR filtering on R32F textures.
 */
let floatLinearExtension: OES_texture_float_linear | null | undefined =
    undefined;

/**
 * {@link ImageObject} represents a 3D volume data.
 */
export class ImageObject extends EnvironmentObject {
    /**
     * Source series UID
     */
    public readonly seriesInstanceUID: string;

    /**
     * The {@link WebGLTexture texture} in which the scan's texture data is stored.\
     * This may be null so unit tests don't require {@link WebGL2RenderingContext WebGL context}.
     */
    private readonly texture: WebGLTexture | null;

    private textureData: Float32Array | null = null;

    private _minValue: number;

    public get windowLevel(): WindowLevel | undefined {
        return this.parent.windowLevel;
    }

    public set windowLevel(windowLevel: WindowLevel) {
        this.parent.windowLevel = windowLevel;
    }

    private readonly _headFirstProne: boolean;

    public get headFirstProne(): boolean {
        return this._headFirstProne;
    }

    private _parent!: Image3D;
    public get parent(): Image3D {
        return this._parent;
    }

    /**
     * The grid defining the spatial properties of this image.
     * May be transformed (e.g., for secondary images with registration).
     */
    private _grid!: Grid;

    public static fromImage3D(
        image3d: Image3D,
        imageSlice: DicomImageSlice,
        transformation?: ReadonlyMat4
    ): ImageObject {
        const img = new ImageObject(
            image3d,
            imageSlice,
            image3d.dicomUid,
            image3d.minValue,
            transformation
        );

        // Take a reference to the Image3D-owned Float32Array.
        // PET unsigned correction is already handled inside Image3D.floatPixelData.
        img.updateTexture(image3d.floatPixelData, image3d.grid);
        img._parent = image3d;
        return img;
    }

    public constructor(
        image3d: Image3D,
        imageSlice: DicomImageSlice,
        seriesUID: string,
        minValue: number,
        transformation?: ReadonlyMat4
    ) {
        super();
        this.seriesInstanceUID = seriesUID;
        this._minValue = minValue;
        this._parent = image3d;
        this._grid = transformation
            ? image3d.grid.transform(transformation)
            : image3d.grid;
        this.texture = this.initializeTexture();
        this.windowLevel = image3d.getDefaultWindowLevel();
        this._headFirstProne = imageSlice.headFirstProne;
    }

    public get minValue(): number {
        return this._minValue;
    }

    public get pixelSize(): ReadonlyVec3 {
        return this.grid.getSize();
    }

    /** Z size in pixels */
    public get sizeZ(): number {
        return this.grid.sizeZ;
    }

    /** Center of the center voxel in patient coordinates (mm). */
    public get centerVoxelCenter(): ReadonlyVec3 {
        return this.grid.toWorldSpace(
            vec3.fromValues(
                Math.floor(this.grid.sizeX / 2) + 0.5,
                Math.floor(this.grid.sizeY / 2) + 0.5,
                Math.floor(this.grid.sizeZ / 2) + 0.5
            )
        );
    }

    public get grid(): Grid {
        return this._grid;
    }

    /**
     * Updates the grid transformation in-place without recreating the object.
     * Use this when only the registration matrix has changed and the pixel
     * data on the GPU is still valid.
     *
     * @param transformation The new world-space transformation to apply to the parent grid.
     */
    public updateGridTransformation(transformation: ReadonlyMat4): void {
        this._grid = this.parent.grid.transform(transformation);
    }

    /**
     * Method to get the value of the image at a given patient coordinate.
     * @param patientCoords The patient coordinate at which to get the image value.
     * @returns The image value at the given patient coordinate or null if the coordinate is outside the bounds of the image.
     */
    public getImageObjectValueByCoordinate(
        patientCoords: ReadonlyVec3
    ): number | null {
        if (!this.textureData || this.textureData.length === 0) {
            return null;
        }
        const indices = this.grid.toVoxelSpace(patientCoords);
        vec3.floor(indices, indices);
        if (!this.grid.includesVoxel(indices)) {
            return null;
        }
        const { sizeX, sizeY } = this.grid;
        const x = indices[0];
        const y = indices[1];
        const z = indices[2];
        const index = z * sizeX * sizeY + y * sizeX + x;
        const imageObjectValue = this.textureData[index];
        if (isNaN(imageObjectValue) || imageObjectValue === undefined) {
            return null;
        }
        return imageObjectValue;
    }

    // Calculate a step along the image grid from the position and direction
    public calculateStep(pos: ReadonlyVec3, dir: ReadonlyVec3): ReadonlyVec3 {
        const stepInGrid = Math.max(
            Math.abs(
                vec3.dot(
                    vec3.scale(vec3.create(), dir, this.grid.resZ),
                    this.grid.getUnitZ()
                )
            ),
            Math.abs(
                vec3.dot(
                    vec3.scale(vec3.create(), dir, this.grid.resY),
                    this.grid.getUnitY()
                )
            ),
            Math.abs(
                vec3.dot(
                    vec3.scale(vec3.create(), dir, this.grid.resX),
                    this.grid.getUnitX()
                )
            )
        );
        const step = vec3.scale(vec3.create(), dir, stepInGrid);
        if (!this.grid.isAxisAligned()) {
            return step;
        }
        const posInPatientspace = this.grid.snapPositionToVoxelCenter(
            vec3.add(vec3.create(), pos, step)
        );

        return vec3.sub(vec3.create(), posInPatientspace, pos);
    }

    public delete(): boolean {
        gl.deleteTexture(this.texture);
        return true;
    }

    private initializeTexture(): WebGLTexture {
        const texture = gl.createTexture();
        if (!texture) {
            throw new Error(`Unable to create WebGLTexture for ImageObject.`);
        }
        gl.bindTexture(gl.TEXTURE_3D, texture);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_BASE_LEVEL, 0);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAX_LEVEL, 0);

        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
        return texture;
    }

    private static probeFloatLinearExtension(): boolean {
        if (floatLinearExtension === undefined) {
            floatLinearExtension = gl.getExtension('OES_texture_float_linear');
            if (!floatLinearExtension) {
                console.warn(
                    'OES_texture_float_linear not available. ' +
                        'PET values above ' +
                        HALF_FLOAT_MAX +
                        ' will be clamped (reduced precision).'
                );
            }
        }
        return floatLinearExtension !== null;
    }

    private updateTexture(texture: Float32Array, grid: Grid) {
        this.textureData = texture;
        gl.bindTexture(gl.TEXTURE_3D, this.texture);

        let internalFormat: GLenum = gl.R16F;
        let uploadData: Float32Array = this.textureData;

        // PET rescaled values can exceed the R16F max of 65504,
        // becoming Infinity in the texture. With LINEAR filtering,
        // Infinity propagates to neighbors as NaN, causing blank
        // rendering artifacts. Use R32F for wide-range images.
        if (this._parent.hasWidePixelRange) {
            if (ImageObject.probeFloatLinearExtension()) {
                internalFormat = gl.R32F;
            } else {
                // Clamp to half-float range to prevent Inf/NaN
                uploadData = new Float32Array(texture.length);
                for (let i = 0; i < texture.length; i++) {
                    uploadData[i] = Math.min(texture[i], HALF_FLOAT_MAX);
                }
            }
        }

        gl.texImage3D(
            gl.TEXTURE_3D,
            0,
            internalFormat,
            grid.sizeX,
            grid.sizeY,
            grid.sizeZ,
            0,
            gl.RED,
            gl.FLOAT,
            uploadData
        );
    }

    public bindTexture(): void {
        gl.bindTexture(gl.TEXTURE_3D, this.texture);
    }
}
